class LensPicker extends HTMLElement {
  connectedCallback() {
    const sectionId = this.dataset.section;
    this.variantRadios = document.querySelector(
      `variant-radios[data-section="${sectionId}"]`
    );
    if (!this.variantRadios) return;

    // Read translated option values from data attributes (set in Liquid)
    this._standardVal = this.dataset.standardVal || 'Standard Lens';
    this._customizedVal = this.dataset.customizedVal || 'Customized Lens';

    this.variants = this.getVariantData();
    this._infoContent = this.closest('.product__info-main_content');
    this._pendingLensType = null;
    this._pendingLensColor = null;

    // Load customized lens media map (variant ID → image URL)
    const clMediaEl = document.querySelector(
      `[data-customized-lens-media="${sectionId}"]`
    );
    this._clMediaMap = clMediaEl ? JSON.parse(clMediaEl.textContent) : {};

    // References for locked image overlay
    const mediaWrapper = document.querySelector(
      `#MainProduct-${sectionId} .product__media-wrapper`
    );
    this._lockedEl = mediaWrapper && mediaWrapper.querySelector('.product__media-locked');
    this._galleryListWrapper = mediaWrapper && mediaWrapper.querySelector('.product__media-list-wrapper');

    const trigger = this._infoContent &&
      this._infoContent.querySelector('.select-lenses-trigger');
    if (trigger) trigger.addEventListener('click', () => this.open());

    const closeBtn = this.querySelector('.lens-picker__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    const addToCartBtn = this.querySelector('.lens-picker__add-to-cart');
    if (addToCartBtn) addToCartBtn.addEventListener('click', () => this.addToCart());

    this.querySelectorAll('.lens-picker__row').forEach((row) => {
      row.addEventListener('click', (e) => this.onRowClick(e, row));
    });

    this.querySelectorAll('.lens-picker__swatch').forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onSwatchClick(swatch);
      });
    });

    this.variantRadios.addEventListener('change', () => {
      if (!this._ownChange) this.syncFromVariant();
    });

    this.syncFromVariant();
  }

  open() {
    if (!this._infoContent) return;
    // Save current option2/option3 so we can restore on close
    this._savedOption2 = this._getCheckedValue(1);
    this._savedOption3 = this._getCheckedValue(2);
    // Default to Standard Lens when opening
    this._pendingLensType = this._standardVal;
    this._pendingLensColor = this._savedOption3;
    this._highlightPending();
    this.style.removeProperty('display');
    this._infoContent.classList.add('lens-picker-mode');
  }

  close() {
    if (!this._infoContent) return;
    this._hideLockedImage();
    this._infoContent.classList.remove('lens-picker-mode');
  }

  addToCart() {
    if (!this._pendingLensType) return;

    const currentColor = this._getCheckedValue(0);
    const lensColor = this._pendingLensColor;

    // Find the exact variant from our local data.
    // Only match option3 (lens color) for Customized Lens — Standard/Polarized ignore it.
    const isCustomized = this._pendingLensType === this._customizedVal;
    const variant = this.variants.find(
      (v) =>
        v.option1 === currentColor &&
        v.option2 === this._pendingLensType &&
        (!isCustomized || lensColor == null || v.option3 === lensColor)
    );
    if (!variant) return;

    const sectionId = this.dataset.section;
    const form = document.querySelector(`#product-form-${sectionId}`);
    if (!form) return;

    // Set variant ID directly on the hidden form input
    const hiddenId = form.querySelector('input[name="id"]');
    if (hiddenId) hiddenId.value = variant.id;

    // Click submit, re-enabling it first in case it was disabled
    const submitBtn = form.querySelector('[name="add"]');
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.click();
    }
  }

  onRowClick(e, row) {
    const lensType = row.dataset.lensType;
    if (lensType === this._customizedVal) return;

    this._pendingLensType = lensType;
    // Preserve current option3 so we can find the right variant on add-to-cart
    this._pendingLensColor = this._getCheckedValue(2);

    this._hideLockedImage();

    // Update gallery to show the correct variant image for the current color + lens type
    const currentColor = this._getCheckedValue(0);
    const variant = this.variants.find(
      (v) => v.option1 === currentColor && v.option2 === lensType
    );
    if (variant && variant.featured_media) {
      this._jumpToVariantMedia(variant);
    }

    this.updateRowHighlight(row);
    this.querySelectorAll('.lens-picker__swatch').forEach((s) =>
      s.classList.remove('lens-picker__swatch--selected')
    );
    const colorNameEl = this.querySelector('.lens-picker__selected-color-name');
    if (colorNameEl) colorNameEl.textContent = '';
  }

  onSwatchClick(swatch) {
    const variantId = parseInt(swatch.dataset.variantId, 10);
    const variant = this.variants.find((v) => v.id === variantId);
    if (!variant) return;

    this._pendingLensType = variant.option2;
    this._pendingLensColor = variant.option3;

    this.querySelectorAll('.lens-picker__swatch').forEach((s) =>
      s.classList.remove('lens-picker__swatch--selected')
    );
    swatch.classList.add('lens-picker__swatch--selected');

    const colorNameEl = this.querySelector('.lens-picker__selected-color-name');
    if (colorNameEl) colorNameEl.textContent = variant.option3;

    const customizedRow = this.querySelector('.lens-picker__row--customized');
    if (customizedRow) this.updateRowHighlight(customizedRow);

    this._updateProductMedia(variant);
  }

  _updateProductMedia(variant) {
    const imageUrl = this._clMediaMap[variant.id];
    if (imageUrl) {
      this._showLockedImage(imageUrl, variant.option3);
    } else if (variant.featured_media) {
      this._hideLockedImage();
      this._jumpToVariantMedia(variant);
    }
  }

  _jumpToVariantMedia(variant) {
    const sectionId = this.dataset.section;
    const mediaId = `${sectionId}-${variant.featured_media.id}`;
    const wrapper = document.querySelector(
      `#MainProduct-${sectionId} .product__media-wrapper`
    );
    if (!wrapper) return;

    const listEl = wrapper.querySelector('.js-media-list');
    if (!listEl) return;

    const slide = wrapper.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;

    if (listEl.swiper) {
      listEl.swiper.slideTo(listEl.swiper.slides.indexOf(slide), 0);
    } else {
      const scrollContainer = listEl.closest('.product__main');
      if (scrollContainer) {
        const targetTop =
          slide.getBoundingClientRect().top -
          scrollContainer.getBoundingClientRect().top +
          scrollContainer.scrollTop;
        scrollContainer.scrollTo({ top: targetTop, behavior: 'instant' });
      }
    }
  }

  _showLockedImage(url, alt) {
    if (!this._lockedEl || !this._galleryListWrapper) return;
    const img = this._lockedEl.querySelector('img');
    if (img) {
      img.src = url;
      img.alt = alt || '';
    }
    this._galleryListWrapper.style.display = 'none';
    this._lockedEl.style.display = '';
  }

  _hideLockedImage() {
    if (!this._lockedEl || !this._galleryListWrapper) return;
    this._lockedEl.style.display = 'none';
    this._galleryListWrapper.style.display = '';
  }

  _getCheckedValue(fieldsetIndex) {
    const fieldsets = this.variantRadios.querySelectorAll('fieldset');
    const fs = fieldsets[fieldsetIndex];
    if (!fs) return null;
    const checked = fs.querySelector('input:checked');
    return checked ? checked.value : null;
  }

  _setInput(fieldsetIndex, value) {
    const fieldsets = this.variantRadios.querySelectorAll('fieldset');
    const fs = fieldsets[fieldsetIndex];
    if (!fs) return;
    const input = Array.from(fs.querySelectorAll('input')).find((i) => i.value === value);
    if (input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _highlightPending() {
    if (!this._pendingLensType) return;
    const rows = this.querySelectorAll('.lens-picker__row');
    let matched = null;
    rows.forEach((row) => {
      if (row.dataset.lensType === this._pendingLensType) matched = row;
    });
    if (matched) this.updateRowHighlight(matched);

    if (this._pendingLensType === this._customizedVal && this._pendingLensColor) {
      this.querySelectorAll('.lens-picker__swatch').forEach((s) => {
        if (s.dataset.colorName === this._pendingLensColor && s.style.display !== 'none') {
          s.classList.add('lens-picker__swatch--selected');
          const colorNameEl = this.querySelector('.lens-picker__selected-color-name');
          if (colorNameEl) colorNameEl.textContent = this._pendingLensColor;
        }
      });
    }
  }

  updateRowHighlight(activeRow) {
    this.querySelectorAll('.lens-picker__row').forEach((r) =>
      r.classList.remove('lens-picker__row--selected')
    );
    activeRow.classList.add('lens-picker__row--selected');
  }

  syncFromVariant() {
    const colorFieldset = this.variantRadios.querySelector('fieldset');
    if (!colorFieldset) return;
    const checkedColor = colorFieldset.querySelector('input:checked');
    if (!checkedColor) return;
    const currentColor = checkedColor.value;

    this.querySelectorAll('.lens-picker__swatch').forEach((swatch) => {
      swatch.style.display = swatch.dataset.frameColor === currentColor ? '' : 'none';
    });

    const standardVariant = this.variants.find(
      (v) => v.option1 === currentColor && v.option2 === this._standardVal
    );

    // Update price diffs and visibility for all non-standard rows
    this.querySelectorAll('.lens-picker__row[data-lens-type]').forEach((row) => {
      const lensType = row.dataset.lensType;
      if (lensType === this._standardVal || lensType === this._customizedVal) return;
      const variant = this.variants.find(
        (v) => v.option1 === currentColor && v.option2 === lensType
      );
      // Hide rows that have no variant for the current frame color
      row.style.display = variant ? '' : 'none';
      if (standardVariant && variant) {
        const diff = variant.price - standardVariant.price;
        const priceEl = row.querySelector('.lens-picker__price');
        if (priceEl) priceEl.textContent = `+${this.formatMoney(diff)}`;
      }
    });

    // If the currently selected row was hidden, reset to standard
    const selectedRow = this.querySelector('.lens-picker__row--selected');
    if (selectedRow && selectedRow.style.display === 'none') {
      selectedRow.classList.remove('lens-picker__row--selected');
      const standardRow = this.querySelector(`.lens-picker__row[data-lens-type="${this._standardVal}"]`);
      if (standardRow) standardRow.classList.add('lens-picker__row--selected');
      this._pendingLensType = this._standardVal;
    }

    // Clear selected swatch if its frame color no longer matches
    const selectedSwatch = this.querySelector('.lens-picker__swatch--selected');
    if (selectedSwatch && selectedSwatch.style.display === 'none') {
      selectedSwatch.classList.remove('lens-picker__swatch--selected');
      const colorNameEl = this.querySelector('.lens-picker__selected-color-name');
      if (colorNameEl) colorNameEl.textContent = '';
      if (this._pendingLensType === this._customizedVal) {
        this._pendingLensType = null;
        this._pendingLensColor = null;
        this._hideLockedImage();
      }
    }

    const customizedRow = this.querySelector('.lens-picker__row--customized');
    const anySwatchSelected = this.querySelector('.lens-picker__swatch--selected');
    if (
      customizedRow &&
      customizedRow.classList.contains('lens-picker__row--selected') &&
      !anySwatchSelected
    ) {
      customizedRow.classList.remove('lens-picker__row--selected');
    }
  }

  formatMoney(cents) {
    const euros = cents / 100;
    const formatted =
      euros % 1 === 0
        ? euros.toLocaleString('pt-PT')
        : euros.toLocaleString('pt-PT', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
    return formatted + '\u00a0€';
  }

  getVariantData() {
    const jsonEl = this.variantRadios.querySelector('[type="application/json"]');
    return jsonEl ? JSON.parse(jsonEl.textContent) : [];
  }
}

customElements.define('lens-picker', LensPicker);
